import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { tv } from "tailwind-variants";
import { cx } from "@/utils/cx";

export type MetricCardTone =
	| "neutral"
	| "info"
	| "success"
	| "warning"
	| "error";

const metricCardVariants = tv({
	base: "flex flex-col rounded-xl border border-border-primary bg-white p-5 text-left shadow-xs",
	variants: {
		selectable: {
			true: "cursor-pointer outline-focus-ring transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:shadow-xs",
			false: "",
		},
		selected: {
			true: "ring-2 ring-brand-600",
			false: "",
		},
	},
	defaultVariants: {
		selectable: false,
		selected: false,
	},
});

const iconContainerVariants = tv({
	base: "mb-5 flex size-12 shrink-0 items-center justify-center rounded-full p-3",
	variants: {
		tone: {
			neutral: "bg-gray-100",
			info: "bg-blue-100",
			success: "bg-green-100",
			warning: "bg-orange-100",
			error: "bg-red-100",
		},
	},
	defaultVariants: { tone: "neutral" },
});

const iconVariants = tv({
	base: "size-5",
	variants: {
		tone: {
			neutral: "text-gray-600",
			info: "text-blue-500",
			success: "text-green-500",
			warning: "text-orange-500",
			error: "text-red-500",
		},
	},
	defaultVariants: { tone: "neutral" },
});

type MetricCardContentProps = {
	/** Ícone decorativo. Fica oculto para tecnologias assistivas. */
	icon: ReactNode;
	/** Título da métrica. */
	title: ReactNode;
	/** Descrição opcional da métrica. */
	subtitle?: ReactNode;
	/** Valor exibido. Valores longos quebram linha; o texto não é cortado. */
	value: ReactNode;
	/** Cor do ícone. A escolha do tom pertence ao domínio. */
	tone?: MetricCardTone;
	/**
	 * Cor base do círculo do ícone (ex.: `#ef4444`). Quando informada,
	 * substitui o fundo derivado de `tone` por essa cor com 10% de opacidade,
	 * sem alterar os demais usos que dependem apenas de `tone`.
	 */
	iconBackground?: string;
	/** Conteúdo à direita do ícone, na linha superior do card. */
	headerAside?: ReactNode;
	/** Conteúdo complementar abaixo do valor. */
	footer?: ReactNode;
	/** Substitui o valor por um indicador de carregamento. */
	isLoading?: boolean;
	/** Texto anunciado durante o carregamento. */
	loadingLabel?: string;
};

/** Card informativo: não entra na ordem de foco nem expõe estado pressionado. */
export type MetricCardInformativeProps = MetricCardContentProps &
	Omit<ComponentPropsWithoutRef<"div">, "title"> & {
		selectable?: false;
	};

/**
 * Card que alterna um filtro. Renderiza um `button` com `aria-pressed`
 * sincronizado com `selected`.
 */
export type MetricCardSelectableProps = MetricCardContentProps &
	Omit<
		ComponentPropsWithoutRef<"button">,
		"title" | "type" | "aria-pressed"
	> & {
		selectable: true;
		/** Indica se o filtro do card está ativo. */
		selected: boolean;
	};

export type MetricCardProps =
	| MetricCardInformativeProps
	| MetricCardSelectableProps;

/**
 * Converte uma cor hexadecimal (`#rgb` ou `#rrggbb`) em `rgba` com a opacidade
 * informada. Retorna `undefined` quando o formato não é reconhecido, deixando
 * o fundo derivado de `tone` prevalecer.
 */
function hexToRgba(hex: string, alpha: number): string | undefined {
	const normalized = hex.trim().replace(/^#/, "");
	const full =
		normalized.length === 3
			? normalized
					.split("")
					.map((char) => char + char)
					.join("")
			: normalized;

	if (!/^[0-9a-fA-F]{6}$/.test(full)) {
		return;
	}

	const r = Number.parseInt(full.slice(0, 2), 16);
	const g = Number.parseInt(full.slice(2, 4), 16);
	const b = Number.parseInt(full.slice(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function MetricCardContent({
	icon,
	title,
	subtitle,
	value,
	tone,
	iconBackground,
	headerAside,
	footer,
	isLoading,
	loadingLabel,
}: MetricCardContentProps) {
	const customBackground = iconBackground
		? hexToRgba(iconBackground, 0.1)
		: undefined;
	const decorativeIcon = (
		<span
			aria-hidden
			className={cx(
				iconContainerVariants({ tone }),
				!!headerAside && "mb-0",
				customBackground && "bg-transparent"
			)}
			data-slot="metric-card-icon"
			style={
				customBackground ? { backgroundColor: customBackground } : undefined
			}
		>
			<span className={cx("block", iconVariants({ tone }))}>{icon}</span>
		</span>
	);

	return (
		<>
			{headerAside ? (
				<span
					className="mb-5 flex items-center justify-between gap-2"
					data-slot="metric-card-header"
				>
					{decorativeIcon}
					{headerAside}
				</span>
			) : (
				decorativeIcon
			)}
			<span className="mb-2 block space-y-1">
				<span className="block font-semibold text-primary text-sm">
					{title}
				</span>
				{subtitle ? (
					<span className="block font-medium text-text-tertiary text-xs">
						{subtitle}
					</span>
				) : null}
			</span>
			{isLoading ? (
				<span className="mt-auto block" data-slot="metric-card-loading">
					<span
						aria-hidden
						className="block h-10 w-24 animate-pulse rounded-md bg-gray-100"
					/>
					<span className="sr-only">{loadingLabel}</span>
				</span>
			) : (
				<span
					className="mt-auto block break-words font-bold text-[32px] text-primary leading-10"
					data-slot="metric-card-value"
				>
					{value}
				</span>
			)}
			{footer ? <span className="mt-2 block">{footer}</span> : null}
		</>
	);
}

/**
 * Card de métrica compartilhado, com a composição dos cards de Gestão de
 * Infrações: `rounded-xl`, borda, sombra leve, `p-5`, ícone em círculo de
 * 48 px, título `sm` semibold, subtítulo `xs` medium e valor de 32 px bold.
 *
 * Sem `selectable`, o card é informativo (`div`). Com `selectable`, vira um
 * botão de alternância com `aria-pressed`, foco visível e anel de seleção.
 */
export function MetricCard(props: MetricCardProps) {
	if (props.selectable) {
		return <SelectableMetricCard {...props} />;
	}

	return <InformativeMetricCard {...props} />;
}

const DEFAULT_LOADING_LABEL = "Carregando valor";

function SelectableMetricCard({
	icon,
	title,
	subtitle,
	value,
	tone,
	iconBackground,
	headerAside,
	footer,
	isLoading = false,
	loadingLabel = DEFAULT_LOADING_LABEL,
	selectable: _selectable,
	selected,
	className,
	...buttonProps
}: MetricCardSelectableProps) {
	return (
		<button
			{...buttonProps}
			aria-busy={isLoading || undefined}
			aria-pressed={selected}
			className={cx(
				metricCardVariants({ selectable: true, selected }),
				className
			)}
			data-selected={selected || undefined}
			data-slot="metric-card"
			type="button"
		>
			<MetricCardContent
				{...{
					icon,
					title,
					subtitle,
					value,
					tone,
					iconBackground,
					headerAside,
					footer,
					isLoading,
				}}
				loadingLabel={loadingLabel}
			/>
		</button>
	);
}

function InformativeMetricCard({
	icon,
	title,
	subtitle,
	value,
	tone,
	iconBackground,
	headerAside,
	footer,
	isLoading = false,
	loadingLabel = DEFAULT_LOADING_LABEL,
	selectable: _selectable,
	className,
	...divProps
}: MetricCardInformativeProps) {
	return (
		<div
			{...divProps}
			aria-busy={isLoading || undefined}
			className={cx(metricCardVariants({ selectable: false }), className)}
			data-slot="metric-card"
		>
			<MetricCardContent
				{...{
					icon,
					title,
					subtitle,
					value,
					tone,
					iconBackground,
					headerAside,
					footer,
					isLoading,
				}}
				loadingLabel={loadingLabel}
			/>
		</div>
	);
}
